import React, { useState, useEffect, useRef, Fragment } from "react";
import { toast } from "react-hot-toast";
import {
  ExclamationTriangleIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import { DocumentManagerV6 } from "../../Media/DocumentManagerV6";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
  effectiveActionsForChecklistItem,
} from "./checklistResponseConfig";
import { ChecklistItemCustomFields } from "./ChecklistItemCustomFields";
import { ChecklistItemGuidance } from "./ChecklistItemGuidance";
import {
  inputFieldsComplete,
  missingRequiredInputFields,
} from "./checklistInputFields";
import clsx from "clsx";

export default function ChecklistItemLinesCompactV2(props) {
  const { t } = useTranslation();
  const {
    checklist,
    items: initialItems = [],
    reportId,
    checklistId,
    editable = false,
    disabled = false,
    isAdmin = false,
    validationErrors = {},
    onItemsUpdate,
    updateChecklistItems, // Function to update checklist items (should accept reportId/checklistId and items array)
  } = props;

  const [items, setItems] = useState(initialItems);
  const [savingItemId, setSavingItemId] = useState(null);
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [selectedItemForDialog, setSelectedItemForDialog] = useState(null);
  const [tempDescription, setTempDescription] = useState("");
  const [isDeviationDialog, setIsDeviationDialog] = useState(false); // Track if dialog was opened from isDeviation change
  const [inputFieldErrorIds, setInputFieldErrorIds] = useState(() => new Set());
  const dialogJustOpenedRef = useRef(false); // Track if dialog just opened to prevent immediate close
  const textareaRef = useRef(null); // Ref for textarea to focus it when dialog opens
  
  // Use ref to track if we're updating from parent to prevent infinite loop
  const isUpdatingFromParent = useRef(false);
  const prevItemsRef = useRef(initialItems);

  // Update items when initialItems change, but only if it's actually different
  useEffect(() => {
    // Compare by JSON string to avoid unnecessary updates
    const prevItemsStr = JSON.stringify(prevItemsRef.current);
    const newItemsStr = JSON.stringify(initialItems);
    
    if (newItemsStr !== prevItemsStr && !isUpdatingFromParent.current) {
      if (initialItems && Array.isArray(initialItems)) {
        setItems(initialItems);
        prevItemsRef.current = initialItems;
      }
    }
  }, [initialItems]);

  // Focus textarea when dialog opens
  useEffect(() => {
    if (descriptionDialogOpen && textareaRef.current) {
      // Use requestAnimationFrame and setTimeout to ensure DOM is ready and dialog is fully rendered
      let timeoutId;
      const frameId = requestAnimationFrame(() => {
        timeoutId = setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            // Move cursor to end of text
            const length = textareaRef.current.value.length;
            textareaRef.current.setSelectionRange(length, length);
            // Ensure textarea is not disabled
            textareaRef.current.disabled = false;
            textareaRef.current.readOnly = false;
          }
        }, 200);
      });
      
      return () => {
        cancelAnimationFrame(frameId);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, [descriptionDialogOpen]);


  // Validate a single item
  const validateItem = (item) => {
    const errors = {};
    
    // Description is required if item is a deviation
    if (item.isDeviation && (!item.description || item.description.trim() === "")) {
      errors.description = "Beskrivelse er påkrevd ved avvik";
    }
    
    // Documents are required if documents_required is true
    if (item.documents_required && (!item.documents || item.documents.length === 0)) {
      errors.documents = "Dokumenter er påkrevd for dette punktet";
    }

    const missingInputFields = missingRequiredInputFields(
      item.input_fields,
      item.input_values
    );
    if (missingInputFields.length > 0) {
      errors.input_fields = t("quality.checklist.item.customFieldRequired");
    }
    
    return errors;
  };

  const requireInputFieldsBeforeOutcome = (item) => {
    if (inputFieldsComplete(item.input_fields, item.input_values)) {
      return true;
    }

    if (item.id) {
      setInputFieldErrorIds((prev) => new Set(prev).add(item.id));
    }
    toast.error(t("quality.checklist.item.customFieldsRequiredBeforeOutcome"));
    return false;
  };

  // Save a single item to API
  const saveItem = async (item) => {
    if (!item.id || (!reportId && !checklistId)) {
      return; // Can't save items without id
    }

    // Validate before saving
    const itemErrors = validateItem(item);
    if (Object.keys(itemErrors).length > 0) {
      // Don't save if validation fails, but show error
      toast.error("Valideringsfeil: " + Object.values(itemErrors).join(", "));
      return;
    }

    setSavingItemId(item.id);
    try {
      // Prepare items array: send only the changed item with full data,
      // and other items with minimal data (id and row_order) to preserve order
      const itemsToSend = items.map((prevItem) => {
        if (prevItem.id === item.id) {
          // Send full data for the changed item
          return {
            id: item.id,
            title: item.title,
            description: item.description,
            isApproved: item.isApproved,
            isDeviation: item.isDeviation,
            is_not_applicable: item.is_not_applicable,
            response_code: item.response_code,
            row_order: item.row_order,
            documents_required: item.documents_required,
            input_values: item.input_values || {},
          };
        } else {
          // Send minimal data for other items (just id and row_order to preserve order)
          return {
            id: prevItem.id,
            row_order: prevItem.row_order,
          };
        }
      });

      // Call the update function passed as prop
      if (updateChecklistItems) {
        const response = await updateChecklistItems(reportId || checklistId, {
          checklist: {
            items: itemsToSend,
          },
        });

        if (response?.data) {
          // API returns the full checklist with items array
          const updatedItemFromApi = response.data.items?.find(i => i.id === item.id);
          
          if (updatedItemFromApi) {
            // Update the item in local state with response data
            isUpdatingFromParent.current = true;
            setItems((prevItems) => {
              const updatedItems = prevItems.map((prevItem) =>
                prevItem.id === item.id ? updatedItemFromApi : prevItem
              );
              
              prevItemsRef.current = updatedItems;
              
              // Notify parent of update
              if (onItemsUpdate) {
                onItemsUpdate(updatedItems);
              }
              
              return updatedItems;
            });
            
            // Reset flag after state update
            setTimeout(() => {
              isUpdatingFromParent.current = false;
            }, 0);
          }
        }
      }
    } catch (error) {
      console.error("Error saving item:", error);
      const errorMessage = error?.response?.data?.errors?.join(", ") ||
                          error?.response?.data?.error ||
                          "Kunne ikke lagre endring";
      toast.error(errorMessage);
    } finally {
      setSavingItemId(null);
    }
  };

  // Debounced save function
  const debouncedSaveItem = debounce(saveItem, 1000);

  const patchItemResponse = (item, key) => {
    const clear = item.response_code === key;
    if (clear) {
      return {
        ...item,
        response_code: null,
        isApproved: false,
        isDeviation: false,
        is_not_applicable: false,
      };
    }
    if (key === "performed" || key === "approved") {
      return {
        ...item,
        response_code: key,
        isApproved: true,
        isDeviation: false,
        is_not_applicable: false,
      };
    }
    if (key === "na") {
      return {
        ...item,
        response_code: "na",
        isApproved: false,
        isDeviation: false,
        is_not_applicable: true,
      };
    }
    return {
      ...item,
      response_code: "deviation",
      isApproved: false,
      isDeviation: true,
      is_not_applicable: false,
    };
  };

  const isActionSelected = (item, key) => {
    if (item.response_code === key) return true;
    if (!item.response_code) {
      if (
        key === "approved" &&
        item.isApproved &&
        !item.isDeviation &&
        !item.is_not_applicable
      )
        return true;
      if (key === "deviation" && item.isDeviation) return true;
      if (key === "na" && item.is_not_applicable) return true;
    }
    return false;
  };

  const handleInputValuesChange = (item, nextValues) => {
    const updatedItem = {
      ...item,
      input_values: nextValues,
    };

    setItems((prevItems) =>
      prevItems.map((prevItem) =>
        prevItem.id === item.id ? updatedItem : prevItem
      )
    );

    if (item.id) {
      setInputFieldErrorIds((prev) => {
        if (!prev.has(item.id)) return prev;
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      debouncedSaveItem(updatedItem);
    }
  };

  // Handle deviation change
  const onDeviationChange = (e, item) => {
    const isDeviationChecked = e.target.checked;
    
    // If deviation is checked, open the note dialog and DON'T save yet
    if (isDeviationChecked) {
      if (!requireInputFieldsBeforeOutcome(item)) {
        return;
      }

      debouncedSaveItem.cancel();
      const updatedItem = {
        ...item,
        isDeviation: true,
        isApproved: false,
        is_not_applicable: false,
        response_code: "deviation",
      };

      // Update local state to show isDeviation as checked
      setItems((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem.id === item.id ? updatedItem : prevItem
        )
      );

      // Open description dialog immediately - don't save until description is saved
      setSelectedItemForDialog(updatedItem);
      setTempDescription(updatedItem.description || "");
      setIsDeviationDialog(true); // Mark that this dialog was opened from isDeviation
      dialogJustOpenedRef.current = true;
      setDescriptionDialogOpen(true);
      // Reset flag after a short delay to allow dialog to fully render
      setTimeout(() => {
        dialogJustOpenedRef.current = false;
      }, 100);
    } else {
      // If unchecking deviation, save immediately
      const updatedItem = {
        ...item,
        isDeviation: false,
        isApproved: false,
        is_not_applicable: false,
        response_code: null,
      };

      setItems((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem.id === item.id ? updatedItem : prevItem
        )
      );

      if (item.id) {
        debouncedSaveItem(updatedItem);
      }
    }
  };

  const onOutcomeSelect = (item, key) => {
    const clearing =
      key !== "deviation" && item.response_code === key;

    if (!clearing && !requireInputFieldsBeforeOutcome(item)) {
      return;
    }

    if (key === "deviation") {
      if (item.isDeviation) {
        onDeviationChange({ target: { checked: false } }, item);
      } else {
        onDeviationChange({ target: { checked: true } }, item);
      }
      return;
    }
    const updatedItem = patchItemResponse(item, key);
    setItems((prevItems) =>
      prevItems.map((prevItem) =>
        prevItem.id === item.id ? updatedItem : prevItem
      )
    );
    if (item.id) {
      debouncedSaveItem(updatedItem);
    }
  };

  // Handle document change (upload, delete, etc.)
  const handleDocumentsChange = (newDocuments, item) => {
    const updatedItem = {
      ...item,
      documents: newDocuments || [],
    };

    setItems((prevItems) =>
      prevItems.map((prevItem) =>
        prevItem.id === item.id ? updatedItem : prevItem
      )
    );

    if (item.id) {
      debouncedSaveItem(updatedItem);
    }
  };

  // Handle description save
  const handleDescriptionSave = () => {
    if (selectedItemForDialog) {
      // Validate that description is not empty if this is a deviation dialog
      if (isDeviationDialog && (!tempDescription || tempDescription.trim() === "")) {
        toast.error("Beskrivelse er påkrevd ved avvik");
        return;
      }

      const updatedItem = {
        ...selectedItemForDialog,
        description: tempDescription,
      };

      setItems((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem.id === selectedItemForDialog.id ? updatedItem : prevItem
        )
      );

      // Now save the item with both isDeviation and description
      if (selectedItemForDialog.id) {
        debouncedSaveItem(updatedItem);
      }
    }
    setDescriptionDialogOpen(false);
    setSelectedItemForDialog(null);
    setTempDescription("");
    setIsDeviationDialog(false);
  };

  const openDescriptionDialog = (item) => {
    setSelectedItemForDialog(item);
    setTempDescription(item.description || "");
    setIsDeviationDialog(false); // Not opened from isDeviation
    dialogJustOpenedRef.current = true;
    setDescriptionDialogOpen(true);
    // Reset flag after a short delay to allow dialog to fully render
    setTimeout(() => {
      dialogJustOpenedRef.current = false;
    }, 100);
  };

  // Handle description dialog cancel
  const handleDescriptionCancel = () => {
    if (isDeviationDialog && selectedItemForDialog) {
      // If dialog was opened from isDeviation, revert isDeviation to false
      const revertedItem = {
        ...selectedItemForDialog,
        isDeviation: false,
      };

      setItems((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem.id === selectedItemForDialog.id ? revertedItem : prevItem
        )
      );
      // Don't save - just revert the state
    }
    setDescriptionDialogOpen(false);
    setSelectedItemForDialog(null);
    setTempDescription("");
    setIsDeviationDialog(false);
  };

  const canUploadImage = (item) => {
    if (!editable || disabled) {
      return false;
    }
    return true;
  };

  // All users can view documents, even when checklist is approved/completed (read-only)
  const canViewDocuments = () => {
    return true;
  };

  const getItemValidationErrors = (item) => {
    const itemIndex = items.findIndex((i) => i.id === item.id);
    if (itemIndex === -1) return null;

    return (
      validationErrors[`items.${itemIndex}`] ||
      validationErrors[`items.${item.id}`] ||
      null
    );
  };

  if (!items || items.length === 0) {
    return (
      <div className="text-center py-6 sm:py-8 text-sm sm:text-base text-gray-500 px-4">
        Ingen sjekkliste-punkter tilgjengelig
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {items.map((item, index) => {
        const itemErrors = getItemValidationErrors(item);
        const isSaving = savingItemId === item.id;

        return (
          <div
            key={item.id || index}
            className={`border rounded-lg ${
              itemErrors
                ? "border-red-300 bg-red-50"
                : isSaving
                ? "border-blue-300 bg-blue-50"
                : "border-gray-200 bg-white"
            }`}
          >
            {/* Header */}
            <div className="p-3 sm:p-4 border-b border-gray-200">
              {/* Mobile: Stacked layout, Desktop: Side by side */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-3">
                {/* Title and Number Row */}
                <div className="flex items-start space-x-2 sm:space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-gray-100 rounded text-xs sm:text-sm font-medium text-gray-600">
                    {index + 1}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm sm:text-base text-gray-900 break-words">
                      {item.title || `Punkt ${index + 1}`}
                    </div>
                    <ChecklistItemGuidance
                      guidance={item.guidance}
                      className="mt-1.5 break-words"
                    />
                    <ChecklistItemCustomFields
                      fields={item.input_fields}
                      values={item.input_values || {}}
                      disabled={disabled || !editable}
                      showErrors={Boolean(item.id && inputFieldErrorIds.has(item.id))}
                      onChange={(nextValues) =>
                        handleInputValuesChange(item, nextValues)
                      }
                    />
                    {item.description && (
                      <div className="text-xs sm:text-sm text-gray-600 mt-1 break-words italic">
                        {item.description}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions Row - On own row on mobile, right side on desktop */}
                <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                  {/* Description Button */}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => openDescriptionDialog(item)}
                      className="p-2 sm:p-1.5 transition-colors duration-200 rounded text-gray-400 hover:text-green-600 hover:bg-green-50 touch-manipulation"
                      title="Rediger beskrivelse"
                    >
                      <PencilIcon className="h-5 w-5 sm:h-4 sm:w-4" />
                    </button>
                  )}

                  {/* Document Manager - Upload and View - Ultra Compact */}
                  <div
                    className={`px-1.5 py-1 sm:px-1 sm:py-0.5 rounded border ${
                      item?.documents_required && item?.documents?.length === 0
                        ? "border-red-300 bg-red-50"
                        : item?.documents?.length > 0
                        ? "border-green-300 bg-green-50"
                        : "border-gray-200"
                    }`}
                  >
                    <DocumentManagerV6
                      subtitle={`Sjekklistepunkt: ${item.title}`}
                      module="checklist_item"
                      moduleParam={item?.id}
                      documents={item?.documents || []}
                      mode="mini"
                      maxDisplay={4}
                      showUpload={canUploadImage(item)}
                      showExisting={canViewDocuments(item)}
                      allowDelete={canUploadImage(item)}
                      allowEdit={canUploadImage(item)}
                      allowCopy={canUploadImage(item) && !!reportId}
                      allowMove={canUploadImage(item) && !!reportId}
                      autoLoad={false}
                      disabled={!canViewDocuments(item) || isSaving}
                      onDocumentsChange={(documents) => handleDocumentsChange(documents, item)}
                      // For checklist items, we can show documents from the report as well
                      filterModule={reportId ? "report" : null}
                      filterModuleParams={reportId ? [reportId] : []}
                      // Pass checklist context for filtering checklist_item documents
                      checklistId={checklistId}
                      checklistItemIds={items.filter(i => i?.id).map(i => i.id)}
                    />
                  </div>

                  {/* Status Buttons */}
                  {!disabled && (
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                      {effectiveActionsForChecklistItem(
                        item,
                        checklist?.checklist_type
                      ).map((key) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => onOutcomeSelect(item, key)}
                          className={clsx(
                            "rounded border px-2 py-1 text-[11px] sm:text-xs font-semibold transition-colors touch-manipulation",
                            isActionSelected(item, key)
                              ? "border-teal-600 bg-teal-600 text-white"
                              : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                          )}
                        >
                          {t(`quality.checklist.actions.${key}`)}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Status Tags when disabled */}
                  {disabled && (
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      {item?.isApproved && (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 sm:px-3 sm:py-1.5 text-xs font-medium text-green-800 border border-green-200">
                          ✓ Godkjent
                        </span>
                      )}
                      {item?.isDeviation && (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 sm:px-3 sm:py-1.5 text-xs font-medium text-red-800 border border-red-200">
                          ⚠️ Avvik
                        </span>
                      )}
                      {item?.is_not_applicable && (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 sm:px-3 sm:py-1.5 text-xs font-medium text-slate-800 border border-slate-200">
                          Ikke aktuelt
                        </span>
                      )}
                      {!item?.isApproved &&
                        !item?.isDeviation &&
                        !item?.is_not_applicable && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 sm:px-3 sm:py-1.5 text-xs font-medium text-gray-600 border border-gray-200">
                          Ikke sjekket
                        </span>
                      )}
                    </div>
                  )}

                  {/* Saving indicator */}
                  {isSaving && (
                    <div className="flex items-center text-blue-600 text-xs sm:text-sm whitespace-nowrap ml-1 sm:ml-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-1 sm:mr-2"></div>
                      <span className="hidden sm:inline">Lagrer...</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Validation Errors */}
              {itemErrors && (
                <div className="mt-3 sm:mt-2 p-2 bg-red-50 border border-red-200 rounded">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-4 w-4 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-xs text-red-700">
                      {itemErrors.description && <div>{itemErrors.description}</div>}
                      {itemErrors.documents && <div>{itemErrors.documents}</div>}
                      {itemErrors.input_fields && <div>{itemErrors.input_fields}</div>}
                    </div>
                  </div>
                </div>
              )}

              {/* Document requirement warning */}
              {item?.documents_required && item?.documents?.length === 0 && (
                <div className="mt-2 text-xs text-red-500 italic">
                  ⚠️ Dokumenter er påkrevd for dette punktet
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Description Dialog */}
      <Transition.Root show={descriptionDialogOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative"
          style={{ zIndex: 2100 }}
          initialFocus={textareaRef}
          onClose={handleDescriptionCancel}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-0 text-center sm:items-center sm:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-t-lg sm:rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all w-full sm:my-8 sm:w-full sm:max-w-2xl sm:px-6">
                  <div>
                    <Dialog.Title as="h3" className="text-base sm:text-lg font-medium leading-6 text-gray-900 mb-3 sm:mb-4">
                      {isDeviationDialog ? "Legg til beskrivelse for avvik" : "Rediger beskrivelse"}
                    </Dialog.Title>
                    {isDeviationDialog && (
                      <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs sm:text-sm text-red-700">
                        Beskrivelse er påkrevd ved avvik
                      </div>
                    )}
                    <div className="mt-2">
                      <textarea
                        ref={textareaRef}
                        value={tempDescription}
                        disabled={false}
                        readOnly={false}
                        onChange={(e) => {
                          setTempDescription(e.target.value);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Ensure textarea is focused and enabled when clicked
                          if (textareaRef.current) {
                            textareaRef.current.focus();
                            textareaRef.current.disabled = false;
                            textareaRef.current.readOnly = false;
                          }
                        }}
                        onFocus={(e) => {
                          e.stopPropagation();
                          // Ensure textarea is enabled when focused
                          if (textareaRef.current) {
                            textareaRef.current.disabled = false;
                            textareaRef.current.readOnly = false;
                          }
                        }}
                        onKeyDown={(e) => {
                          // Allow Escape to close dialog (Dialog component handles this automatically)
                          // Don't stop propagation for other keys to allow normal typing
                        }}
                        className={`w-full border rounded-md p-3 sm:p-2 min-h-[120px] sm:min-h-[150px] resize-y text-base sm:text-sm ${
                          isDeviationDialog && (!tempDescription || tempDescription.trim() === "")
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                        }`}
                        placeholder={isDeviationDialog ? "Beskriv avviket her..." : "Skriv beskrivelse her..."}
                      />
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2 sm:gap-0">
                    <button
                      type="button"
                      onClick={handleDescriptionSave}
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-4 py-3 sm:py-2 text-base sm:text-sm font-semibold text-white shadow-sm hover:bg-blue-500 active:bg-blue-700 touch-manipulation sm:ml-3 sm:w-auto"
                    >
                      Lagre
                    </button>
                    <button
                      type="button"
                      onClick={handleDescriptionCancel}
                      className="inline-flex w-full justify-center rounded-md bg-white px-4 py-3 sm:py-2 text-base sm:text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 active:bg-gray-100 touch-manipulation sm:mt-0 sm:w-auto"
                    >
                      Avbryt
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

    </div>
  );
}


